import { CloudUpload, Delete, Error as ErrorIcon } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import BackendService from 'BackendService';
import { InvestorEntityAllocations } from 'InvestorEntityAllocations';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import { Palette } from 'Palette';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../../components/styles/StyledButtons';
import { StyledDocumentLink } from '../../../components/styles/StyledLink';
import StyledModal from '../../../components/styles/StyledModal';
import StyledSection from '../../../components/styles/StyledSection';
import { StyledHeading, StyledTypography } from '../../../components/styles/styles';
import UploadItem from '../../../components/UploadItem';
import Document from '../../../models/Document';
import FundOpportunity from '../../../models/FundOpportunity';
import Investor from '../../../models/Investor';
import { downloadDoc } from '../../../services/DocUtils';
import DocumentCategorySelect from './DocumentCategorySelect';
import InlineCreateCategoryModal from './InlineCreateCategoryModal';
import { useDocumentCategoryOptions } from './useDocumentCategoryOptions';
import { useInvestorDocumentUpload } from './useInvestorDocumentUpload';

function formatDocumentDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

interface AdminInvestorExtraSectionProps {
  fundOpportunity: FundOpportunity;
  investor?: Investor;
  onDocumentsAdded?: () => void;
  onInvestorUpdate?: (updatedInvestor: Investor) => void;
  /** Bump to force the existing-documents list to re-fetch (e.g. after a statement is added to the library). */
  refreshSignal?: number;
}

export default function AdminInvestorExtraSection({
  fundOpportunity,
  investor,
  onDocumentsAdded,
  onInvestorUpdate,
  refreshSignal,
}: AdminInvestorExtraSectionProps) {
  const [existingDocs, setExistingDocs] = useState<Document[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);

  const { categories, defaultCategoryId, canCreateCategory, createCategoryInline, loadCategories } =
    useDocumentCategoryOptions();

  const backendRef = useRef(new BackendService());

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const assignCreatedRef = useRef<((categoryId: string) => void) | null>(null);

  const openCreateCategory = useCallback((assign: (categoryId: string) => void) => {
    assignCreatedRef.current = assign;
    setCreateModalOpen(true);
  }, []);

  const handleCreateCategory = useCallback(
    async (name: string) => {
      const id = await createCategoryInline(name);
      if (id) assignCreatedRef.current?.(id);
      assignCreatedRef.current = null;
      setCreateModalOpen(false);
    },
    [createCategoryInline],
  );

  const loadExistingDocs = useCallback(async () => {
    const backend = backendRef.current;
    const docs = await (investor
      ? backend.getInvestorDocuments(investor.id)
      : backend.getGenericInvestorDocuments(fundOpportunity.id));
    setExistingDocs(docs);
  }, [fundOpportunity.id, investor]);

  useEffect(() => {
    void loadExistingDocs();
  }, [loadExistingDocs, refreshSignal]);

  const {
    uploadModalOpen,
    setUploadModalOpen,
    uploadItems,
    setUploadItems,
    handleFilesSelected,
    setRowCategory,
    applyCategoryToAll,
    uploadAll,
  } = useInvestorDocumentUpload({
    backendRef,
    investor,
    fundOpportunityId: fundOpportunity.id,
    defaultCategoryId,
    loadExistingDocs,
    loadCategories,
    onDocumentsAdded,
  });

  const removeExisting = async (docId: string) => {
    const backend = backendRef.current;
    await (investor
      ? backend.deleteInvestorDocument(investor.id, docId)
      : backend.deleteInvestorGenericDocument(fundOpportunity.id, docId));
    onDocumentsAdded?.();
    loadExistingDocs();
    void loadCategories();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await removeExisting(deleteTarget.id);
    setDeleteTarget(null);
  };

  const refileExisting = async (docId: string, categoryId: string) => {
    const backend = backendRef.current;
    await (investor
      ? backend.updateInvestorDocumentCategory(investor.id, docId, categoryId)
      : backend.updateGenericInvestorDocumentCategory(fundOpportunity.id, docId, categoryId));
    await loadExistingDocs();
    void loadCategories();
  };

  const renderUploadRow = (item: UploadItem, idx: number) => (
    <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', mb: 2, mt: '1rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <TextField
          label="Title"
          value={item.title}
          fullWidth
          onChange={(e) => {
            const title = e.target.value;
            setUploadItems((prev) => prev.map((it, i) => (i === idx ? { ...it, title } : it)));
          }}
          helperText={`File name: ${item.file.name}`}
        />
        <DocumentCategorySelect
          categories={categories}
          value={item.categoryId ?? defaultCategoryId}
          onChange={(id) => setRowCategory(idx, id)}
          onRequestCreate={
            canCreateCategory
              ? () => openCreateCategory((id) => setRowCategory(idx, id))
              : undefined
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', height: 56 }}>
          {item.status === 'uploading' && <CircularProgress size={20} />}
          {item.status === 'done' && (
            <Typography variant="body2" color="green">
              ✓
            </Typography>
          )}
          {item.status === 'error' && <ErrorIcon color="error" />}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Entity Allocations Section - Only show for specific investors */}
      {investor && investor.status === 'COMMITTED_INVESTOR' && (
        <Box sx={{ mb: 4 }}>
          <InvestorEntityAllocations
            investor={investor}
            fundOpportunityId={fundOpportunity.id}
            onInvestorUpdate={onInvestorUpdate}
          />
        </Box>
      )}

      {/* Documents Section */}
      <Box mt={4}>
        <StyledSection tone="tone1">
          <StyledHeading headerVariant="heading2" reduceBottomPadding>
            {investor ? 'Investor ' : 'Fund '}Documents
          </StyledHeading>
          <StyledHeading headerVariant="subheading" sx={{ color: Palette.default.disabledText }}>
            {investor
              ? 'These will appear in the Fund Information section at the bottom of the Fund Summary screen.'
              : 'These documents will appear in Fund Information presented to all active investors in the Fund.'}
          </StyledHeading>

          <Box sx={{ mt: 2, mb: 3 }}>
            <StyledButton noTopSpacing onClick={() => setUploadModalOpen(true)}>
              Upload New
            </StyledButton>
          </Box>

          <StyledHeading headerVariant="subheading" reduceBottomPadding>
            Existing Documents
          </StyledHeading>
          {existingDocs.length === 0 ? (
            <StyledTypography sx={{ color: Palette.default.disabledText, mb: 2 }}>
              No documents uploaded yet.
            </StyledTypography>
          ) : (
            existingDocs.map((doc) => (
              <Box key={doc.id} display="flex" alignItems="center" gap={3} mb={2}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flex: 1 }}>
                  <StyledDocumentLink tone="tone1" onClick={() => downloadDoc(doc)}>
                    {doc.title}
                  </StyledDocumentLink>
                  {doc.createdAt && (
                    <Typography variant="body2" sx={{ color: Palette.default.text }}>
                      {formatDocumentDate(doc.createdAt)}
                    </Typography>
                  )}
                </Box>
                <DocumentCategorySelect
                  categories={categories}
                  value={doc.categoryId}
                  onChange={(id) => refileExisting(doc.id, id)}
                  onRequestCreate={
                    canCreateCategory
                      ? () => openCreateCategory((id) => refileExisting(doc.id, id))
                      : undefined
                  }
                />
                <IconButton
                  onClick={() => setDeleteTarget(doc)}
                  sx={{ color: Palette.warning.buttonBg }}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))
          )}

          <StyledModal
            open={uploadModalOpen}
            onClose={() => {
              setUploadItems([]);
              setUploadModalOpen(false);
            }}
            title="Upload Documents"
            subtitle={
              !investor
                ? 'These documents will be available to ALL investors in this fund.'
                : undefined
            }
            actions={
              <>
                <StyledButton
                  noTopSpacing
                  buttonVariant="inactiveOrCancel"
                  sx={SECONDARY_BUTTON_HOVER_SX}
                  onClick={() => {
                    setUploadItems([]);
                    setUploadModalOpen(false);
                  }}
                  disabled={uploadItems.length === 0}
                >
                  Cancel
                </StyledButton>
                <StyledButton noTopSpacing onClick={uploadAll} disabled={uploadItems.length === 0}>
                  Upload
                </StyledButton>
              </>
            }
          >
            <Dropzone
              onDrop={(acceptedFiles: File[]) => handleFilesSelected(acceptedFiles)}
              multiple
            >
              {({ getRootProps, getInputProps, isDragActive }) => {
                const rootProps = getRootProps();
                const inputProps = getInputProps();

                return (
                  <Box
                    {...rootProps}
                    sx={{
                      border: `2px dashed ${Palette.tone1.border}`,
                      borderRadius: 2,
                      padding: '1.5rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: isDragActive
                        ? Palette.tone1.background
                        : Palette.tone2.background,
                      color: Palette.tone1.text,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {/* MUST BE INSIDE rootProps */}
                    <input {...inputProps} style={{ display: 'none' }} />
                    <CloudUpload sx={{ fontSize: 36, color: Palette.default.disabledText }} />
                    <StyledTypography fontWeight={500} sx={{ color: Palette.default.disabledText }}>
                      {isDragActive
                        ? 'Drop the files here…'
                        : 'Drag & drop files here or click to upload'}
                    </StyledTypography>
                    <StyledTypography
                      fontSize="0.8rem"
                      sx={{ color: Palette.default.disabledText }}
                    >
                      PDF, DOCX, XLSX, etc. supported
                    </StyledTypography>
                  </Box>
                );
              }}
            </Dropzone>
            {uploadItems.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: '1rem' }}>
                <StyledTypography sx={{ color: Palette.default.disabledText }}>
                  Apply category to all:
                </StyledTypography>
                <DocumentCategorySelect
                  categories={categories}
                  value={undefined}
                  onChange={applyCategoryToAll}
                  onRequestCreate={
                    canCreateCategory
                      ? () => openCreateCategory((id) => applyCategoryToAll(id))
                      : undefined
                  }
                />
              </Box>
            )}
            <Box>{uploadItems.map((item, idx) => renderUploadRow(item, idx))}</Box>
          </StyledModal>

          <InlineCreateCategoryModal
            open={createModalOpen}
            onClose={() => {
              assignCreatedRef.current = null;
              setCreateModalOpen(false);
            }}
            onCreate={handleCreateCategory}
          />

          <StyledModal
            open={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            title="Delete Document"
            subtitle={
              deleteTarget ? `Are you sure you want to delete "${deleteTarget.title}"?` : undefined
            }
            actions={
              <>
                <StyledButton
                  noTopSpacing
                  buttonVariant="inactiveOrCancel"
                  sx={SECONDARY_BUTTON_HOVER_SX}
                  onClick={() => setDeleteTarget(null)}
                >
                  Cancel
                </StyledButton>
                <StyledButton noTopSpacing buttonVariant="warning" onClick={confirmDelete}>
                  Delete
                </StyledButton>
              </>
            }
          >
            <></>
          </StyledModal>
        </StyledSection>
      </Box>
    </Box>
  );
}
